import apiClient from './apiClient';
import * as Sentry from '@sentry/react';

// Types for user feedback system
export interface UserFeedback {
  id?: string;
  userId?: string;
  email: string;
  sentryErrorId?: string;
  category: 'bug' | 'feature' | 'performance' | 'other';
  priority: 'low' | 'medium' | 'high';
  summary: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  status: 'open' | 'in_progress' | 'resolved';
  createdAt?: Date;
  resolvedAt?: Date;
  userAgent?: string;
  url?: string;
}

export interface FeedbackFormData {
  category: UserFeedback['category'];
  priority: UserFeedback['priority'];
  summary: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  email?: string;
}

export interface TechnicalContext {
  sentryErrorId?: string;
  userAgent: string;
  url: string;
  timestamp: string;
  errorMessage?: string;
  errorStack?: string;
}

class UserFeedbackService {
  /**
   * Submit user feedback with optional error context
   */
  async submitFeedback(
    feedbackData: FeedbackFormData,
    technicalContext?: TechnicalContext
  ): Promise<UserFeedback> {
    try {
      // Prepare payload for backend
      const payload: Partial<UserFeedback> = {
        ...feedbackData,
        sentryErrorId: technicalContext?.sentryErrorId,
        userAgent: technicalContext?.userAgent || navigator.userAgent,
        url: technicalContext?.url || window.location.href,
        status: 'open',
      };

      // Submit to backend database
      const response = await apiClient.post('/api/feedback', payload);
      const savedFeedback: UserFeedback = response.data;

      // Also submit to Sentry as additional context if we have an error ID
      if (technicalContext?.sentryErrorId) {
        try {
          // Use Sentry.withScope for user context and feedback data
          Sentry.withScope((scope) => {
            scope.setUser({
              email: savedFeedback.email,
              username: savedFeedback.email.split('@')[0],
            });
            scope.setTag('feedback_category', savedFeedback.category);
            scope.setTag('feedback_priority', savedFeedback.priority);
            scope.setExtra('feedback_summary', savedFeedback.summary);
            scope.setExtra('feedback_description', savedFeedback.description);
            scope.setExtra('feedback_steps', savedFeedback.stepsToReproduce);
            scope.setExtra('linked_error_id', technicalContext.sentryErrorId);

            // Capture as an info event linked to the original error
            Sentry.captureMessage(`User Feedback Submitted: ${savedFeedback.summary}`, 'info');
          });
        } catch (sentryError) {
          console.warn('Failed to submit feedback context to Sentry:', sentryError);
          // Don't fail the entire operation if Sentry submission fails
        }
      }

      return savedFeedback;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw new Error('Failed to submit feedback. Please try again later.');
    }
  }

  /**
   * Get user's feedback history
   */
  async getUserFeedback(userId?: string): Promise<UserFeedback[]> {
    try {
      const response = await apiClient.get('/api/feedback/user', {
        params: userId ? { userId } : {},
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user feedback:', error);
      throw new Error('Failed to load feedback history.');
    }
  }

  /**
   * Update feedback status (for admin/support use)
   */
  async updateFeedbackStatus(
    feedbackId: string,
    status: UserFeedback['status'],
    resolution?: string
  ): Promise<UserFeedback> {
    try {
      const response = await apiClient.put(`/api/feedback/${feedbackId}`, {
        status,
        resolution,
        resolvedAt: status === 'resolved' ? new Date() : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update feedback status:', error);
      throw new Error('Failed to update feedback status.');
    }
  }

  /**
   * Delete user feedback (GDPR compliance)
   */
  async deleteFeedback(feedbackId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/feedback/${feedbackId}`);
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      throw new Error('Failed to delete feedback.');
    }
  }

  /**
   * Quick error report (simplified flow for error boundaries)
   */
  async reportError(
    email: string,
    errorId: string,
    userDescription: string,
    errorContext?: {
      errorMessage?: string;
      url?: string;
      userAgent?: string;
    }
  ): Promise<UserFeedback> {
    const feedbackData: FeedbackFormData = {
      category: 'bug',
      priority: 'medium',
      summary: 'Error occurred while using the application',
      description: userDescription || 'An unexpected error occurred.',
      email,
    };

    const technicalContext: TechnicalContext = {
      sentryErrorId: errorId,
      userAgent: errorContext?.userAgent || navigator.userAgent,
      url: errorContext?.url || window.location.href,
      timestamp: new Date().toISOString(),
      errorMessage: errorContext?.errorMessage,
    };

    return this.submitFeedback(feedbackData, technicalContext);
  }
}

// Export singleton instance
export const userFeedbackService = new UserFeedbackService();
export default userFeedbackService;
